import React from "react";
import { useApolloClient, useMutation, useQuery, gql } from "@apollo/client";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Search,
  Trash2,
  Upload,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OdrLoadingSpinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthenticationState } from "@/store/useAuthenticationState";
import { toast } from "@/components/ui/toast";

const GET_DOCUMENTS_LIBRARY_BOOTSTRAP = gql`
  query GetDocumentsLibraryBootstrap {
    documentsLibraryBootstrap {
      permissions
      canDelete
      canDownload
      canEdit
      canSearch
      canUpload
      canViewExternal
      canViewInternal
      programs {
        id
        name
      }
      programTypes {
        id
        name
      }
      programScopes {
        id
        name
      }
      documentTypes {
        id
        name
      }
    }
  }
`;

const GET_DOCUMENTS_LIBRARY_SEARCH = gql`
  query GetDocumentsLibrarySearch(
    $filter: DocumentsLibrarySearchFilterInput
    $page: Int
    $perPage: Int
    $query: String
    $sort: String
  ) {
    documentsLibrarySearch(
      filter: $filter
      page: $page
      perPage: $perPage
      query: $query
      sort: $sort
    ) {
      currentPage
      lastPage
      nextPage
      perPage
      prevPage
      total
      data {
        id
        publicId
        name
        access
        createdAt
        programs {
          id
          name
        }
        documentsTypes {
          id
          name
        }
        file {
          originalFileName
          documentExtension
          version
        }
      }
    }
  }
`;

const GET_DOCUMENTS_LIBRARY_DOCUMENT = gql`
  query GetDocumentsLibraryDocument($publicId: String!) {
    documentsLibraryDocument(publicId: $publicId) {
      id
      publicId
      name
      access
      createdAt
      userName
      programs {
        id
        name
      }
      programTypes {
        id
        name
      }
      programScopes {
        id
        name
      }
      documentsTypes {
        id
        name
      }
      file {
        originalFileName
        documentExtension
        fileKey
        fileUrl
        version
      }
    }
  }
`;

const DOWNLOAD_DOCUMENTS_LIBRARY_DOCUMENT = gql`
  query DownloadDocumentsLibraryDocument($publicId: String!) {
    documentsLibraryDownload(publicId: $publicId) {
      name
      type
      contents
    }
  }
`;

const UPLOAD_DOCUMENTS_LIBRARY_DOCUMENT = gql`
  mutation UploadDocumentsLibraryDocument($input: DocumentsLibraryUploadInput!) {
    uploadDocumentsLibraryDocument(input: $input) {
      id
      publicId
      name
    }
  }
`;

const DELETE_DOCUMENTS_LIBRARY_DOCUMENT = gql`
  mutation DeleteDocumentsLibraryDocument($publicId: String!) {
    deleteDocumentsLibraryDocument(publicId: $publicId) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

type LookupItem = {
  id: number;
  name: string;
};

type DocumentsLibraryBootstrap = {
  canDelete: boolean;
  canDownload: boolean;
  canEdit: boolean;
  canSearch: boolean;
  canUpload: boolean;
  canViewExternal: boolean;
  canViewInternal: boolean;
  documentTypes: LookupItem[];
  permissions: string[];
  programs: LookupItem[];
  programScopes: LookupItem[];
  programTypes: LookupItem[];
};

type DocumentsLibraryRow = {
  access: string;
  createdAt?: string | null;
  documentsTypes: LookupItem[];
  file?: {
    documentExtension?: string | null;
    originalFileName?: string | null;
    version?: number | null;
  } | null;
  id: number;
  name: string;
  programs: LookupItem[];
  publicId: string;
};

type DocumentsLibraryDetail = {
  access: string;
  createdAt?: string | null;
  documentsTypes: LookupItem[];
  file?: {
    documentExtension?: string | null;
    fileKey?: string | null;
    fileUrl?: string | null;
    originalFileName?: string | null;
    version?: number | null;
  } | null;
  id: number;
  name: string;
  programScopes: LookupItem[];
  programTypes: LookupItem[];
  programs: LookupItem[];
  publicId: string;
  userName?: string | null;
};

type DocumentsLibrarySearchResponse = {
  documentsLibrarySearch: {
    currentPage: number;
    data: DocumentsLibraryRow[];
    lastPage: number;
    nextPage?: number | null;
    perPage: number;
    prevPage?: number | null;
    total: number;
  };
};

type FilterState = {
  documentType: string[];
  program: string[];
  programScope: string[];
  programType: string[];
};

type UploadState = {
  access: "external" | "internal";
  documentTypeIds: number[];
  file: File | null;
  name: string;
  programIds: number[];
  programScopeIds: number[];
  programTypeIds: number[];
};

type UploadDialogState = "form" | "success" | "error";

const FILTER_STORAGE_KEY = "selectedPrograms";
const PER_PAGE_STORAGE_KEY = "documentsLibraryPerPage";
const QUERY_STORAGE_KEY = "querySession";
const SORT_STORAGE_KEY = "sort";
const TOTAL_STORAGE_KEY = "totalDocuments";

const DEFAULT_FILTERS: FilterState = {
  documentType: [],
  program: [],
  programScope: [],
  programType: [],
};

const DEFAULT_UPLOAD_STATE: UploadState = {
  access: "internal",
  documentTypeIds: [],
  file: null,
  name: "",
  programIds: [],
  programScopeIds: [],
  programTypeIds: [],
};

const loadStoredJson = <T,>(storageKey: string, fallback: T) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  const rawValue = window.sessionStorage.getItem(storageKey);
  if (!rawValue) {
    return fallback;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch (_error) {
    return fallback;
  }
};

const loadStoredString = (storageKey: string, fallback: string) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  return window.sessionStorage.getItem(storageKey) || fallback;
};

const loadStoredNumber = (storageKey: string, fallback: number) => {
  const parsed = Number(loadStoredString(storageKey, `${fallback}`));
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return fallback;
};

const normalizeStoredFilters = (value: unknown): FilterState => {
  if (!value || typeof value !== "object") {
    return DEFAULT_FILTERS;
  }

  const raw = value as Record<string, unknown>;
  return {
    documentType: Array.isArray(raw.documentType) ? raw.documentType.map((item) => `${item || ""}`.trim()).filter(Boolean) : [],
    program: Array.isArray(raw.program) ? raw.program.map((item) => `${item || ""}`.trim()).filter(Boolean) : [],
    programScope: Array.isArray(raw.programScope) ? raw.programScope.map((item) => `${item || ""}`.trim()).filter(Boolean) : [],
    programType: Array.isArray(raw.programType) ? raw.programType.map((item) => `${item || ""}`.trim()).filter(Boolean) : [],
  };
};

const loadStoredFilters = () => {
  return normalizeStoredFilters(loadStoredJson(FILTER_STORAGE_KEY, DEFAULT_FILTERS));
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString("en-US", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatShortDate = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const getFileIcon = (extension?: string | null) => {
  const normalized = `${extension || ""}`.trim().toLowerCase();
  if (["csv", "xls", "xlsx"].includes(normalized)) {
    return FileSpreadsheet;
  }
  return FileText;
};

const getSelectedIdsFromLabels = (items: LookupItem[], selectedLabels: string[]) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const labelSet = new Set((Array.isArray(selectedLabels) ? selectedLabels : []).map((value) => `${value || ""}`.trim()));
  return items
    .filter((item) => labelSet.has(item.name))
    .map((item) => item.id);
};

const getLookupText = (items: LookupItem[]) => {
  if (!Array.isArray(items) || items.length === 0) {
    return "N/A";
  }

  return items.map((item) => item.name).join(", ");
};

const fileToBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      if (!result) {
        reject(new Error("Failed to read file."));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });
};

const downloadBase64File = (contents: string, fileName: string, contentType: string) => {
  const encoded = `${contents || ""}`.trim();
  if (!encoded) {
    throw new Error("File content is empty.");
  }

  const binary = window.atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  const blob = new Blob([bytes], { type: contentType || "application/octet-stream" });
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName || "document";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

const FilterGroup = ({
  items,
  selectedLabels,
  title,
  onClear,
  onToggle,
}: {
  items: LookupItem[];
  selectedLabels: string[];
  title: string;
  onClear: () => void;
  onToggle: (itemName: string) => void;
}) => {
  return (
    <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
      <div className="flex items-center justify-between border-b border-[var(--neutral-gray-30)] px-3 py-2">
        <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
          {title}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[12px] text-[var(--azure-primary)]"
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
      <div className="max-h-48 overflow-y-auto px-3 py-2">
        {items.length === 0 && (
          <div className="text-[12px] text-[var(--neutral-gray-80)]">No options.</div>
        )}
        {items.map((item) => (
          <label key={item.id} className="flex items-center gap-2 py-1 text-[12px] text-[var(--neutral-gray-120)]">
            <Checkbox
              checked={selectedLabels.includes(item.name)}
              onCheckedChange={() => onToggle(item.name)}
              className="border-[var(--neutral-gray-50)] data-[state=checked]:border-[var(--azure-primary)] data-[state=checked]:bg-[var(--azure-primary)]"
            />
            <span>{item.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const UploadSelectionGroup = ({
  items,
  selectedIds,
  title,
  onToggle,
}: {
  items: LookupItem[];
  selectedIds: number[];
  title: string;
  onToggle: (itemId: number) => void;
}) => {
  return (
    <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-3 py-2">
      <div className="pb-2 text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
        {title}
      </div>
      <div className="max-h-32 overflow-y-auto">
        {items.map((item) => (
          <label key={item.id} className="flex items-center gap-2 py-1 text-[12px] text-[var(--neutral-gray-120)]">
            <Checkbox
              checked={selectedIds.includes(item.id)}
              onCheckedChange={() => onToggle(item.id)}
              className="border-[var(--neutral-gray-50)] data-[state=checked]:border-[var(--azure-primary)] data-[state=checked]:bg-[var(--azure-primary)]"
            />
            <span>{item.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const DocumentsLibrary = () => {
  const client = useApolloClient();
  const { authenticationState } = useAuthenticationState();
  const [searchInput, setSearchInput] = React.useState(() => loadStoredString(QUERY_STORAGE_KEY, ""));
  const [activeQuery, setActiveQuery] = React.useState(() => loadStoredString(QUERY_STORAGE_KEY, ""));
  const [sort, setSort] = React.useState(() => loadStoredString(SORT_STORAGE_KEY, "Latest"));
  const [perPage, setPerPage] = React.useState(() => loadStoredNumber(PER_PAGE_STORAGE_KEY, 25));
  const [filters, setFilters] = React.useState<FilterState>(() => loadStoredFilters());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [uploadState, setUploadState] = React.useState<UploadState>(DEFAULT_UPLOAD_STATE);
  const [uploadDialogState, setUploadDialogState] = React.useState<UploadDialogState>("form");
  const [uploadDialogMessage, setUploadDialogMessage] = React.useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const uploadFileInputRef = React.useRef<HTMLInputElement | null>(null);

  const bootstrapQuery = useQuery<{ documentsLibraryBootstrap: DocumentsLibraryBootstrap }>(
    GET_DOCUMENTS_LIBRARY_BOOTSTRAP,
    {
      fetchPolicy: "network-only",
      skip: !authenticationState,
    }
  );

  const bootstrap = bootstrapQuery.data?.documentsLibraryBootstrap;
  const resolvedFilters = {
    documentTypeIds: getSelectedIdsFromLabels(bootstrap?.documentTypes || [], filters.documentType),
    programIds: getSelectedIdsFromLabels(bootstrap?.programs || [], filters.program),
    programScopeIds: getSelectedIdsFromLabels(bootstrap?.programScopes || [], filters.programScope),
    programTypeIds: getSelectedIdsFromLabels(bootstrap?.programTypes || [], filters.programType),
  };

  const canViewLibrary = Boolean(
    bootstrap?.canViewExternal ||
      bootstrap?.canViewInternal
  );
  const canOpenLibrary = Boolean(canViewLibrary || bootstrap?.canUpload);

  const searchQuery = useQuery<DocumentsLibrarySearchResponse>(
    GET_DOCUMENTS_LIBRARY_SEARCH,
    {
      variables: {
        filter: resolvedFilters,
        page: currentPage,
        perPage,
        query: activeQuery,
        sort,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      skip: !canViewLibrary,
    }
  );

  const detailQuery = useQuery<{ documentsLibraryDocument: DocumentsLibraryDetail }>(
    GET_DOCUMENTS_LIBRARY_DOCUMENT,
    {
      variables: {
        publicId: selectedDocumentId,
      },
      fetchPolicy: "network-only",
      skip: !selectedDocumentId,
    }
  );

  const [uploadDocumentMutation] = useMutation(UPLOAD_DOCUMENTS_LIBRARY_DOCUMENT);
  const [deleteDocumentMutation] = useMutation(DELETE_DOCUMENTS_LIBRARY_DOCUMENT);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(QUERY_STORAGE_KEY, activeQuery);
  }, [activeQuery]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(SORT_STORAGE_KEY, sort);
  }, [sort]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(PER_PAGE_STORAGE_KEY, `${perPage}`);
  }, [perPage]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);
  const result = searchQuery.data?.documentsLibrarySearch;

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(TOTAL_STORAGE_KEY, `${result?.total || 0}`);
  }, [result?.total]);

  const toggleFilterItem = (filterKey: keyof FilterState, itemName: string) => {
    setCurrentPage(1);
    setFilters((current) => {
      const currentValues = current[filterKey];
      if (currentValues.includes(itemName)) {
        return {
          ...current,
          [filterKey]: currentValues.filter((value) => value !== itemName),
        };
      }

      return {
        ...current,
        [filterKey]: [...currentValues, itemName],
      };
    });
  };

  const clearFilterGroup = (filterKey: keyof FilterState) => {
    setCurrentPage(1);
    setFilters((current) => ({
      ...current,
      [filterKey]: [],
    }));
  };

  const toggleUploadSelection = (field: keyof UploadState, itemId: number) => {
    setUploadState((current) => {
      const currentValues = current[field];
      if (!Array.isArray(currentValues)) {
        return current;
      }
      if (currentValues.includes(itemId)) {
        return {
          ...current,
          [field]: currentValues.filter((value) => value !== itemId),
        };
      }

      return {
        ...current,
        [field]: [...currentValues, itemId],
      };
    });
  };

  const openUploadDialog = () => {
    setUploadState(DEFAULT_UPLOAD_STATE);
    setUploadDialogState("form");
    setUploadDialogMessage("");
    setUploadOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setUploadState(DEFAULT_UPLOAD_STATE);
    setUploadDialogState("form");
    setUploadDialogMessage("");
  };

  const handleRetryUpload = () => {
    setUploadDialogState("form");
    setUploadDialogMessage("");
  };

  const handleUploadFile = (file: File | null) => {
    setUploadState((current) => ({
      ...current,
      file,
    }));
  };

  const handleUploadDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] || null;
    if (!file) {
      return;
    }
    handleUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadState.name.trim()) {
      toast.error("Document title is required.");
      return;
    }

    if (!uploadState.file) {
      toast.error("Select a file to upload.");
      return;
    }

    setIsUploading(true);
    try {
      const fileContentBase64 = await fileToBase64(uploadState.file);
      await uploadDocumentMutation({
        variables: {
          input: {
            access: uploadState.access,
            documentTypeIds: uploadState.documentTypeIds,
            fileContentBase64,
            fileMimeType: uploadState.file.type || "application/octet-stream",
            fileName: uploadState.file.name,
            name: uploadState.name.trim(),
            programIds: uploadState.programIds,
            programScopeIds: uploadState.programScopeIds,
            programTypeIds: uploadState.programTypeIds,
            published: 1,
          },
        },
      });
      setUploadDialogMessage("File uploaded successfully");
      setUploadDialogState("success");
      if (canViewLibrary) {
        void searchQuery.refetch();
      }
    } catch (error: any) {
      setUploadDialogMessage(error?.message || "File storage error");
      setUploadDialogState("error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedDocumentId) {
      return;
    }

    setIsDownloading(true);
    try {
      const response = await client.query<{
        documentsLibraryDownload: {
          contents: string;
          name: string;
          type: string;
        };
      }>({
        fetchPolicy: "no-cache",
        query: DOWNLOAD_DOCUMENTS_LIBRARY_DOCUMENT,
        variables: {
          publicId: selectedDocumentId,
        },
      });
      const payload = response.data?.documentsLibraryDownload;
      if (!payload?.contents) {
        throw new Error("Document download returned no content.");
      }
      downloadBase64File(payload.contents, payload.name, payload.type);
    } catch (error: any) {
      toast.error(error?.message || "Unable to download document.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocumentId) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDocumentMutation({
        variables: {
          publicId: selectedDocumentId,
        },
      });
      toast.success("Document removed.");
      setDeleteConfirmOpen(false);
      setSelectedDocumentId(null);
      void searchQuery.refetch();
    } catch (error: any) {
      toast.error(error?.message || "Unable to remove document.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    setActiveQuery(searchInput.trim());
  };

  if (bootstrapQuery.loading && !bootstrap) {
    return <OdrLoadingSpinner message="Loading documents library" />;
  }

  if (bootstrapQuery.error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-sm text-[var(--semantic-error)]">
            {bootstrapQuery.error.message || "Unable to load the documents library."}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canOpenLibrary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Documents Library</CardTitle>
          <CardDescription>Access Denied</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-[var(--neutral-gray-100)]">
            You do not have permission to view the documents library.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader className="gap-2 border-b-0 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)]">
                <FileText className="h-5 w-5 text-[var(--azure-primary)]" />
              </div>
              <div>
                <CardTitle>Documents</CardTitle>
                <CardDescription>Library</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            {bootstrap?.canSearch ? (
              <form onSubmit={handleSearchSubmit} className="flex min-w-0 flex-1 items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--neutral-gray-80)]" />
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search documents"
                    className="pl-8 text-[12px]"
                  />
                </div>
                <Button type="submit" size="sm" className="h-8 px-3 text-[12px]">
                  Search
                </Button>
              </form>
            ) : (
              <div className="h-8 min-w-0 flex-1" />
            )}

            {bootstrap?.canUpload && (
              <Button size="sm" className="h-8 gap-2 px-3 text-[12px]" onClick={openUploadDialog}>
                <Upload className="h-3.5 w-3.5" />
                Upload Document
              </Button>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[var(--neutral-gray-80)]" />
                <CardTitle className="text-base">Filters</CardTitle>
              </div>
              <CardDescription>Refine the document library.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <FilterGroup
                title="Program"
                items={bootstrap?.programs || []}
                selectedLabels={filters.program}
                onClear={() => clearFilterGroup("program")}
                onToggle={(itemName) => toggleFilterItem("program", itemName)}
              />
              <FilterGroup
                title="Program Type"
                items={bootstrap?.programTypes || []}
                selectedLabels={filters.programType}
                onClear={() => clearFilterGroup("programType")}
                onToggle={(itemName) => toggleFilterItem("programType", itemName)}
              />
              <FilterGroup
                title="Program Scope"
                items={bootstrap?.programScopes || []}
                selectedLabels={filters.programScope}
                onClear={() => clearFilterGroup("programScope")}
                onToggle={(itemName) => toggleFilterItem("programScope", itemName)}
              />
              <FilterGroup
                title="Document Type"
                items={bootstrap?.documentTypes || []}
                selectedLabels={filters.documentType}
                onClear={() => clearFilterGroup("documentType")}
                onToggle={(itemName) => toggleFilterItem("documentType", itemName)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="gap-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Result</CardTitle>
                  <CardDescription>
                    About {result?.total ?? 0} documents in this library
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--neutral-gray-80)]">
                    MOST POPULAR
                  </div>
                  <div className="w-[180px]">
                    <Label className="pb-1 text-[12px] font-medium text-[var(--neutral-gray-80)]">
                      Sort by:
                    </Label>
                    <Select
                      value={sort}
                      onValueChange={(value) => {
                        setSort(value);
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Latest">Latest</SelectItem>
                        <SelectItem value="Oldest">Oldest</SelectItem>
                        <SelectItem value="AZ">Alphabetical, A to Z</SelectItem>
                        <SelectItem value="ZA">Alphabetical, Z to A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardHeader>
          <CardContent className="space-y-3">
              {!canViewLibrary && (
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-6 text-sm text-[var(--neutral-gray-80)]">
                  You do not have permission to browse library results. Upload remains available if your role allows it.
                </div>
              )}

              {canViewLibrary && searchQuery.loading && (
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-6 text-sm text-[var(--neutral-gray-80)]">
                  Loading documents...
                </div>
              )}

              {canViewLibrary && searchQuery.error && (
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] px-4 py-3 text-sm text-[var(--semantic-error)]">
                  {searchQuery.error.message || "Unable to load documents."}
                </div>
              )}

              {canViewLibrary && !searchQuery.loading && !searchQuery.error && (result?.data || []).length === 0 && (
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-6 text-sm text-[var(--neutral-gray-80)]">
                  No documents match the current search and filters.
                </div>
              )}

              {canViewLibrary && (result?.data || []).map((item) => {
                const FileIcon = getFileIcon(item.file?.documentExtension);

                return (
                  <button
                    key={item.publicId}
                    type="button"
                    onClick={() => setSelectedDocumentId(item.publicId)}
                    className="w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-4 py-3 text-left transition-colors hover:border-[var(--neutral-gray-50)] hover:bg-[var(--surface-muted)]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <FileIcon className="h-4 w-4 flex-shrink-0 text-[var(--azure-primary)]" />
                          <div className="truncate text-sm font-semibold text-[var(--neutral-gray-120)]">
                            {item.name}
                          </div>
                        </div>
                        <div className="pt-1 text-[12px] text-[var(--neutral-gray-80)]">
                          {item.file?.originalFileName || "No file name"}
                        </div>
                        <div className="flex flex-wrap gap-1 pt-2">
                          {item.programs.map((program) => (
                            <Badge
                              key={`${item.publicId}-program-${program.id}`}
                              variant="outline"
                              className="border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] px-1.5 py-0 text-[12px] font-medium text-[var(--neutral-gray-100)]"
                            >
                              {program.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="min-w-[110px] text-right text-[12px] text-[var(--neutral-gray-80)]">
                        <div>Upload: {formatShortDate(item.createdAt)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--neutral-gray-30)] pt-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-[12px]"
                    disabled={!canViewLibrary || !result?.prevPage}
                    onClick={() => {
                      if (!result?.prevPage) {
                        return;
                      }
                      setCurrentPage(result.prevPage);
                    }}
                  >
                    Prev
                  </Button>
                  {canViewLibrary && Array.from({ length: result?.lastPage || 1 }, (_item, index) => index + 1).map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      variant={pageNumber === (result?.currentPage || 1) ? "default" : "outline"}
                      size="sm"
                      className="h-8 min-w-8 px-2 text-[12px]"
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-[12px]"
                    disabled={!canViewLibrary || !result?.nextPage}
                    onClick={() => {
                      if (!result?.nextPage) {
                        return;
                      }
                      setCurrentPage(result.nextPage);
                    }}
                  >
                    Next
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[12px] font-medium text-[var(--neutral-gray-80)]">
                    Items per page:
                  </Label>
                  <div className="w-[120px]">
                    <Select
                      value={`${perPage}`}
                      onValueChange={(value) => {
                        setPerPage(Number(value));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="h-8 text-[12px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={uploadOpen}
        onOpenChange={(open: boolean) => {
          if (open) {
            setUploadOpen(true);
            return;
          }
          closeUploadDialog();
        }}
      >
        <DialogContent
          className="max-h-[82vh] overflow-y-auto"
          contentHeight="auto"
          contentMaxHeight="82vh"
          contentMaxWidth="960px"
          contentWidth="92vw"
        >
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription />
          </DialogHeader>

          {uploadDialogState === "form" && (
            <>
              <div className="grid gap-4 py-1 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="documents-library-title">Title</Label>
                  <Input
                    id="documents-library-title"
                    value={uploadState.name}
                    onChange={(event) =>
                      setUploadState((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    className="text-[12px]"
                  />
                </div>

                <UploadSelectionGroup
                  title="Programs"
                  items={bootstrap?.programs || []}
                  selectedIds={uploadState.programIds}
                  onToggle={(itemId) => toggleUploadSelection("programIds", itemId)}
                />
                <UploadSelectionGroup
                  title="Programs Types"
                  items={bootstrap?.programTypes || []}
                  selectedIds={uploadState.programTypeIds}
                  onToggle={(itemId) => toggleUploadSelection("programTypeIds", itemId)}
                />
                <UploadSelectionGroup
                  title="Programs Scopes"
                  items={bootstrap?.programScopes || []}
                  selectedIds={uploadState.programScopeIds}
                  onToggle={(itemId) => toggleUploadSelection("programScopeIds", itemId)}
                />
                <UploadSelectionGroup
                  title="Documents Types"
                  items={bootstrap?.documentTypes || []}
                  selectedIds={uploadState.documentTypeIds}
                  onToggle={(itemId) => toggleUploadSelection("documentTypeIds", itemId)}
                />

                <div className="space-y-2 md:col-span-2">
                  <Label>Accessibility</Label>
                  <Select
                    value={uploadState.access}
                    onValueChange={(value: "external" | "internal") =>
                      setUploadState((current) => ({
                        ...current,
                        access: value,
                      }))
                    }
                  >
                    <SelectTrigger className="text-[12px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="external">External</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <input
                    ref={uploadFileInputRef}
                    id="documents-library-file"
                    type="file"
                    className="hidden"
                    aria-label="Browse file from your computer"
                    onChange={(event) => handleUploadFile(event.target.files?.[0] || null)}
                  />
                  {uploadState.file ? (
                    <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-4 text-[12px] text-[var(--neutral-gray-120)]">
                      {uploadState.file.name}
                    </div>
                  ) : (
                    <div
                      className="rounded-[var(--border-radius-large)] border border-dashed border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-6 py-10 text-center"
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={handleUploadDrop}
                    >
                      <Upload className="mx-auto h-8 w-8 text-[var(--azure-primary)]" />
                      <div className="pt-3 text-sm font-medium text-[var(--neutral-gray-120)]">
                        Drop your files here
                      </div>
                      <div className="pt-1 text-[12px] text-[var(--neutral-gray-80)]">
                        <button
                          type="button"
                          className="font-medium text-[var(--azure-primary)]"
                          onClick={() => uploadFileInputRef.current?.click()}
                        >
                          Browse file
                        </button>{" "}
                        from your computer
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {uploadState.file && (
                <DialogFooter>
                  <Button variant="outline" onClick={closeUploadDialog}>
                    Cancel
                  </Button>
                  <Button onClick={() => void handleUpload()} disabled={isUploading}>
                    {isUploading ? "Uploading..." : "Continue"}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}

          {uploadDialogState !== "form" && (
            <div className="space-y-6 py-8 text-center">
              <div className="text-base font-semibold text-[var(--neutral-gray-120)]">
                {uploadDialogMessage}
              </div>
              <DialogFooter className="sm:justify-center">
                <Button variant="outline" onClick={closeUploadDialog}>
                  Close
                </Button>
                {uploadDialogState === "error" && (
                  <Button onClick={handleRetryUpload}>
                    Retry
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedDocumentId)}
        onOpenChange={(open: boolean) => {
          if (open) {
            return;
          }
          setSelectedDocumentId(null);
        }}
      >
        <DialogContent
          className="max-h-[82vh] overflow-y-auto"
          contentHeight="auto"
          contentMaxHeight="82vh"
          contentMaxWidth="900px"
          contentWidth="88vw"
        >
          <DialogHeader>
            <DialogTitle>{detailQuery.data?.documentsLibraryDocument?.name || "Document"}</DialogTitle>
            <DialogDescription>Document information</DialogDescription>
          </DialogHeader>

          {detailQuery.loading && (
            <div className="py-6 text-sm text-[var(--neutral-gray-80)]">Loading document details...</div>
          )}

          {detailQuery.error && (
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] px-4 py-3 text-sm text-[var(--semantic-error)]">
              {detailQuery.error.message || "Unable to load document details."}
            </div>
          )}

          {detailQuery.data?.documentsLibraryDocument && (
            <div className="grid gap-4 py-1 md:grid-cols-2">
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Original File Name
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {detailQuery.data.documentsLibraryDocument.file?.originalFileName || "N/A"}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Created At
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {formatDate(detailQuery.data.documentsLibraryDocument.createdAt)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Original File Extension
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {detailQuery.data.documentsLibraryDocument.file?.documentExtension || "N/A"}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  User
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {detailQuery.data.documentsLibraryDocument.userName || "N/A"}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Programs
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {getLookupText(detailQuery.data.documentsLibraryDocument.programs)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Programs Types
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {getLookupText(detailQuery.data.documentsLibraryDocument.programTypes)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Programs Scopes
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {getLookupText(detailQuery.data.documentsLibraryDocument.programScopes)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Document Type
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {getLookupText(detailQuery.data.documentsLibraryDocument.documentsTypes)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Accessibility
                </div>
                <div className="text-sm capitalize text-[var(--neutral-gray-120)]">
                  {detailQuery.data.documentsLibraryDocument.access || "N/A"}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">
                  Actual Version
                </div>
                <div className="text-sm text-[var(--neutral-gray-120)]">
                  {detailQuery.data.documentsLibraryDocument.file?.version || 1}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {bootstrap?.canDelete && (
              <Button
                variant="outline"
                className="border-[var(--semantic-error-border-soft)] text-[var(--semantic-error)]"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isDeleting}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeleting ? "Removing..." : "Remove"}
              </Button>
            )}
            {bootstrap?.canDownload && (
              <Button onClick={() => void handleDownload()} disabled={isDownloading}>
                <Download className="mr-2 h-4 w-4" />
                {isDownloading ? "Downloading..." : "Download"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure to delete this document?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-[var(--semantic-error)] hover:bg-[var(--semantic-error)]/90"
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : "Yes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DocumentsLibrary;
